// src/components/TimerBanner.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Flame, Radio, ArrowRight } from 'lucide-react';

const TimerBanner = () => {
  const [banner, setBanner] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const q = query(collection(db, 'timerBanners'), where('active', '==', true));
        const snap = await getDocs(q);
        const banners = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        const now = new Date();
        const valid = banners.find(b => {
          const hasStart = !!b.startDate;
          const hasEnd = !!b.endDate;
          const start = hasStart ? (b.startDate.toDate ? b.startDate.toDate() : new Date(b.startDate)) : null;
          const end = hasEnd ? (b.endDate.toDate ? b.endDate.toDate() : new Date(b.endDate)) : null;
          if (start && now < start) return false;
          if (end && now > end) return false;
          return true;
        });

        setBanner(valid || null);
      } catch (error) {
        console.error('Error fetching timer banner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  useEffect(() => {
    if (!banner?.endDate) return;

    const interval = setInterval(() => {
      const end = banner.endDate?.toDate ? banner.endDate.toDate() : new Date(banner.endDate);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setExpired(true);
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(interval);
  }, [banner]);

  if (loading || !banner || expired) return null;

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="w-full max-w-7xl mx-auto px-3 md:px-6 py-6 md:py-10">
      <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl anim-fadeInUp group">

        {/* Background Image */}
        <img
          src={banner.image}
          alt={banner.title || 'Flash Sale'}
          className="w-full h-[500px] sm:h-[520px] md:h-[560px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-[2000ms]"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/55"></div>

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3 md:px-8 text-white">

          {/* Discount Label with Icon */}
          {banner.discountLabel && (
            <div className="flex items-center gap-1.5 md:gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-red-500/90 backdrop-blur-md mb-3 md:mb-4 anim-fadeInUp shadow-lg">
              <Flame size={14} className="md:w-4 md:h-4 text-white" fill="currentColor" />
              <span className="text-[10px] md:text-xs lg:text-sm font-bold tracking-widest uppercase">
                {banner.discountLabel}
              </span>
            </div>
          )}

          {/* Title */}
          {banner.title && (
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-wide mb-4 md:mb-8 anim-fadeInUp delay-100 leading-tight max-w-4xl uppercase drop-shadow-2xl px-2">
              {banner.title}
            </h2>
          )}

          {/* Description */}
          {banner.description && (
            <p className="text-xs sm:text-sm md:text-lg mb-5 md:mb-8 max-w-2xl opacity-90 anim-fadeInUp delay-150 px-2">
              {banner.description}
            </p>
          )}

          {/* Countdown */}
          {banner.endDate && (
            <div className="w-full max-w-2xl mb-6 md:mb-10 anim-fadeInUp delay-200">
              <div className="grid grid-cols-4 gap-1 sm:gap-2 md:gap-6 lg:gap-8 justify-items-center">
                {[
                  { val: timeLeft.days, label: 'Days' },
                  { val: timeLeft.hours, label: 'Hours' },
                  { val: timeLeft.minutes, label: 'Minutes' },
                  { val: timeLeft.seconds, label: 'Seconds' },
                ].map((t, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-light tabular-nums leading-none">
                      {pad(t.val)}
                    </div>
                    <p className="text-[8px] sm:text-[9px] md:text-xs lg:text-sm font-semibold mt-1.5 md:mt-3 uppercase tracking-[0.15em] opacity-80">
                      {t.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Button */}
          {banner.buttonText && banner.buttonLink && (
            <Link
              to={banner.buttonLink}
              className="inline-flex items-center gap-2 text-xs sm:text-sm md:text-lg lg:text-xl font-bold tracking-wider anim-fadeInUp delay-300 hover:opacity-80 transition uppercase px-4 md:px-6 py-2 md:py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20"
            >
              {banner.buttonText} <ArrowRight size={16} className="md:w-5 md:h-5" />
            </Link>
          )}

        </div>

        {/* Top-right LIVE badge with Icon */}
        <div className="absolute top-3 right-3 md:top-5 md:right-5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-[10px] md:text-xs font-bold flex items-center gap-1.5 border border-white/30">
          <Radio size={12} className="md:w-3.5 md:h-3.5 text-red-500 animate-pulse" />
          <span>LIVE</span>
        </div>

      </div>
    </section>
  );
};

export default TimerBanner;